interface CascadeSelections {
  sites?: string[];
  componentsMajor?: string[];
}

interface CascadeOptionsShape {
  turbineModels: string[];
  componentsMinor: string[];
  modelToSites?: Record<string, string[]>;
  majorComponentByComponent?: Record<string, string>;
}

/**
 * Narrows the Turbine Model and Sub-Component option lists based on currently
 * selected Sites and Major Components. Empty selection ⇒ no narrowing for that
 * dimension. Falls back to full list if dependency maps are missing.
 */
export function applyCascadingFilters<T extends CascadeOptionsShape>(
  options: T,
  selections: CascadeSelections,
): T {
  const sites = selections.sites ?? [];
  const major = selections.componentsMajor ?? [];

  let { turbineModels, componentsMinor } = options;

  if (sites.length > 0 && options.modelToSites) {
    const siteSet = new Set(sites);
    turbineModels = turbineModels.filter((model) => {
      const linked = options.modelToSites?.[model];
      return !!linked && linked.some((site) => siteSet.has(site));
    });
  }

  if (major.length > 0 && options.majorComponentByComponent) {
    const majorSet = new Set(major);
    componentsMinor = componentsMinor.filter((component) => {
      const parent = options.majorComponentByComponent?.[component];
      return !!parent && majorSet.has(parent);
    });
  }

  return { ...options, turbineModels, componentsMinor };
}

interface ChildSelectionShape {
  turbineModels?: string[];
  componentsMinor?: string[];
}

/**
 * Drops Turbine Model / Sub-Component selections that are no longer present in
 * the available option lists (e.g. after a parent Site or Major Component
 * change made them invalid).
 */
export function pruneOrphanedSelections<T extends ChildSelectionShape>(
  filters: T,
  available: { turbineModels: string[]; componentsMinor: string[] },
): T {
  const validModels = new Set(available.turbineModels);
  const validMinor = new Set(available.componentsMinor);
  const next: T = { ...filters };
  if (filters.turbineModels) {
    next.turbineModels = filters.turbineModels.filter((m) => validModels.has(m));
  }
  if (filters.componentsMinor) {
    next.componentsMinor = filters.componentsMinor.filter((c) => validMinor.has(c));
  }
  return next;
}
